import { useState } from 'react';
import { Gift, Loader2 } from 'lucide-react';

import { useHolidayStore } from '@/store/useHolidayStore';
import config from '@/config';

export function WrappedVerificationModal() {
  const {
    showWrappedModal,
    setShowWrappedModal,
    setWrappedVerified,
    verifyingWrapped,
    setVerifyingWrapped,
    setSelectedGiftId,
  } = useHolidayStore();

  const [tweetUrl, setTweetUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!showWrappedModal) return null;

  const handleVerify = async () => {
    if (!tweetUrl.trim()) {
      setError('Please paste your Wrapped tweet link');
      return;
    }

    // Basic URL validation for Twitter/X
    const tweetRegex = /^https?:\/\/(twitter\.com|x\.com)\/\w+\/status\/\d+/i;
    if (!tweetRegex.test(tweetUrl.trim())) {
      setError('Please enter a valid Twitter/X post link');
      return;
    }

    setError(null);
    setVerifyingWrapped(true);

    try {
      // Verify the wrapped tweet using the holiday campaign endpoint (no activity check required)
      const response = await fetch(`${config.API_BASE_URL}/holiday/verify-wrapped-share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ tweetUrl: tweetUrl.trim() }),
      });

      const data = await response.json();

      // Check for success
      if (data.code === 0 && data.data?.success) {
        setWrappedVerified(true);
        setShowWrappedModal(false);
        setTweetUrl('');
      } else {
        setError(data.message || data.data?.error || 'Could not verify your Wrapped tweet. Please make sure you shared your Wrapped results and tagged @junkfun_.');
      }
    } catch (err) {
      setError('Failed to verify tweet. Please try again.');
    } finally {
      setVerifyingWrapped(false);
    }
  };

  const handleClose = () => {
    setShowWrappedModal(false);
    setSelectedGiftId(null);
    setTweetUrl('');
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center pointer-events-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 pointer-events-auto cursor-pointer animate-modal-backdrop-in"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-gradient-to-b from-[#1a1a2e] to-[#16213e] rounded-xl p-5 max-w-sm w-full mx-4 border border-orange-500/30 shadow-2xl animate-modal-backdrop-in font-outfit pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gift icon decoration */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2">
          <Gift className="w-14 h-14 text-orange-400" />
        </div>

        <h2 className="text-xl font-bold text-center text-orange-400 mt-6 mb-2">
          Open Your Present!
        </h2>

        <p className="text-gray-300 text-center mb-4 text-sm">
          To open this present, first share your Junk.Fun Wrapped on Twitter/X and paste the link below.
        </p>

        <div className="space-y-3">
          {/* Requirements */}
          <div className="bg-black/30 rounded-lg p-3 text-sm">
            <p className="text-gray-400 mb-2">Your Wrapped tweet should:</p>
            <ul className="text-gray-300 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Share your Junk.Fun Wrapped stats
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Tag <span className="text-orange-400 font-bold">@junkfun_</span>
              </li>
            </ul>
          </div>

          {/* Input */}
          <input
            type="url"
            value={tweetUrl}
            onChange={(e) => {
              setTweetUrl(e.target.value);
              setError(null);
            }}
            placeholder="https://x.com/you/status/..."
            className="w-full bg-black/40 border border-gray-600 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
            disabled={verifyingWrapped}
          />

          {/* Error */}
          {error && (
            <p className="text-red-400 text-xs text-center">{error}</p>
          )}

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="flex-1 px-3 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors text-sm"
              disabled={verifyingWrapped}
            >
              Cancel
            </button>
            <button
              onClick={handleVerify}
              disabled={verifyingWrapped || !tweetUrl.trim()}
              className="flex-1 px-3 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 text-white font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {verifyingWrapped ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </span>
              ) : (
                'Verify & Open'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
